import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../../_alert';
import { NgxSpinnerService } from 'ngx-spinner';
import { DepartmentService } from '../../services/jrni/department.service';
import { DatastoreService } from '../../services/datastore.service';
import { ServicesService } from '../../services/jrni/services.service';
import { TranslateService } from '@ngx-translate/core';
import { BasketService } from '../../services/jrni/basket.service';
import { ApiService } from '../../services/jrni/api.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { AppConfig } from "../../app.config";
import { HeyCentricService } from '../../services/hey-centric.service';
import { QuestionsService } from '../../services/questions.service';
import { interval, Subscription } from 'rxjs';

// HeyCentric return page (book-after-payment). The gateway redirects here
// after the customer pays (or backs out); this page never checks the basket
// out itself — the server-side HeyCentric worker does that once the gateway
// confirms — it only hydrates journey state from the pre-basket record and
// polls /pre-basket/find until the row reaches PAID (or fails).
@Component({
  selector: 'app-verify-payment',
  templateUrl: './verify-payment.component.html',
  styleUrls: ['./verify-payment.component.scss']
})
export class VerifyPaymentComponent implements OnInit, OnDestroy {

  companyId: any = null;
  pageTitle: string;
  verifyPageText: string;
  selectedSlot: any;
  selectedService: any;
  selectedLocation: any;
  company: any;
  parent: any;
  config: any;
  basket: string;
  serviceItems: string[] = [];
  amount: any;
  receiptId: string;
  bookingMade = false;
  nr: any = window['newrelic'];
  // Poll budget: interval(2000) × retries. Overridable per tenant via
  // journey_config.ceremonies.payment_verify_retries.
  retries = 100;
  maxRetriesReached = false;
  paymentFailed = false;
  paymentAlreadyPaid = false;
  paymentCancelled = false;
  webhookResponseStatus = '';
  failed = false;
  chargeId = '';
  receipt = '';
  ref = '';

  private subscriptions: Subscription[] = [];
  private pollSubscription: Subscription | null = null;

  constructor(
    private router: Router,
    private departmentService: DepartmentService,
    private alertService: AlertService,
    private spinner: NgxSpinnerService,
    private datastoreService: DatastoreService,
    private heyCentricService: HeyCentricService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private servicesService: ServicesService,
    private basketService: BasketService,
    private apiService: ApiService,
    private availabilityService: AvailabilityService,
    private questionsService: QuestionsService
  ) { }

  async ngOnInit() {
    this.spinner.show();
    // Reaching the verify page means the HeyCentric round-trip concluded
    // (success or its own Back/Cancel button). Clear the payment-in-flight
    // marker so it can't later trigger a false journey reset. The browser Back
    // button never reaches this page, so its marker survives for app.component
    // to handle.
    sessionStorage.removeItem('heycentric_payment_pending');

    const appConfig: any = await this.apiService.getConfig(AppConfig.settings.core.company_id);
    this.config = appConfig;
    this.datastoreService.config = appConfig;

    const configuredRetries = this.config?.journey_config?.ceremonies?.payment_verify_retries;
    if (configuredRetries !== undefined && configuredRetries !== null && configuredRetries !== '') {
      const parsed = parseInt(configuredRetries);
      if (!isNaN(parsed) && parsed > 0) {
        this.retries = parsed;
        console.log(`Retries set to ${this.retries}`);
      }
    }

    this.subscriptions.push(this.route.params.subscribe(async (params) => {
      console.log('url params', params);
      if (params.basket) {
        this.basket = params.basket;
      }
      if (params.serviceItems) {
        // The return URL carries a leading-comma list (",123,456"); drop the empties.
        this.serviceItems = params.serviceItems.split(',').filter((s) => s);
      }
      if (params.company) {
        this.companyId = params.company;
      }
      if (!this.basket || this.serviceItems.length === 0) {
        console.error('verify: missing basket or service items in route', params);
        this.spinner.hide();
        this.paymentFailed = true;
        this.pageTitle = this.translateService.instant('COMMON.PAYMENT_FAILED_TITLE');
        return;
      }
      console.log('begin verify');

      // The user abandoned the payment on the HeyCentric gateway. That happens
      // three ways: the browser Back button or HeyCentric's own "Back" button
      // (both return here with no query params), or HeyCentric's "Cancel"
      // button (returns with ?Rc=C plus Pmt/SID/hash). In all three cases
      // destroy the basket and restart on the correct landing page for the
      // journey they were on (agent-aware).
      const returnParams = this.route.snapshot.queryParams;
      if (Object.keys(returnParams).length < 1 || returnParams['Rc'] === 'C') {
        this.paymentCancelled = true;
        this.pageTitle = this.translateService.instant('COMMON.PAYMENT_CANCELLED_TITLE');
        await this.restartJourneyAfterCancel();
        return;
      }

      this.subscriptions.push(this.route.queryParams.subscribe((queryParams) => {
        this.pageTitle = 'Verifying payment';

        this.departmentService.getParentDepartment().then((dept) => {
          this.parent = dept;
          this.departmentService.getChildDepartments(dept).then(async (children) => {
            for (const location of children) {
              if (location.id == this.companyId) {
                this.company = location;
                await this.departmentService.setLocation(location);
                this.selectedLocation = location;
                break;
              }
            }

            // Online (customer) return: AuthCode / Rc / RecNo / Pmt
            if (queryParams['AuthCode'] && queryParams['Rc'] && queryParams['RecNo'] && queryParams['Pmt']) {
              console.log('AuthCode', queryParams['AuthCode'], 'Rc', queryParams['Rc'], 'RecNo', queryParams['RecNo']);
              this.amount = parseFloat(queryParams['Pmt']);
              if (queryParams['Rc'] === 'A') {
                for (const serviceItem of this.serviceItems) {
                  await this.hydrateAndPoll(serviceItem, queryParams['AuthCode'], queryParams['RecNo'], false);
                }
              } else {
                console.error(`Purchase was not successful for ${this.basket}.`);
                this.spinner.hide();
                this.paymentFailed = true;
                this.alertService.error(this.translateService.instant('PAYMENT.ERROR'));
              }
            }

            // Telephony (Agent Referred Payment) return: return / payResult / authCode / refNo
            if (queryParams['return'] && queryParams['payResult']) {
              if (queryParams['payResult'] === 'Authorised') {
                this.datastoreService.adminJourney = true;
                console.log('payResult', queryParams['payResult'], 'authCode', queryParams['authCode'], 'refNo', queryParams['refNo']);
                for (const serviceItem of this.serviceItems) {
                  await this.hydrateAndPoll(serviceItem, queryParams['authCode'], queryParams['refNo'], true);
                }
              } else {
                console.error(`ARP purchase was not authorised for ${this.basket}: ${queryParams['payResult']}`);
                this.spinner.hide();
                this.paymentFailed = true;
                this.alertService.error(this.translateService.instant('PAYMENT.ERROR'));
              }
            }
          }).catch((err) => {
            console.log(err);
            this.spinner.hide();
            this.paymentFailed = true;
            this.alertService.error(this.translateService.instant('PAYMENT.ERROR'));
          });
        }).catch((err) => {
          console.log(err);
          this.spinner.hide();
          this.paymentFailed = true;
          this.alertService.error(this.translateService.instant('PAYMENT.ERROR'));
        });
      }));
    }));
  }

  ngOnDestroy(): void {
    this.stopPolling();
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.subscriptions = [];
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
  }

  // Load the pre-basket record for one service item, hydrate the journey state
  // the confirmation page needs from it, then poll until the worker has made
  // the booking. Shared by the online and ARP return legs — they differ only
  // in which query params carry the auth code / receipt.
  private async hydrateAndPoll(serviceItem: string, authCode: string, receiptNo: string, agent: boolean): Promise<void> {
    try {
      console.log('serviceItem', serviceItem, 'cid', this.companyId);
      const purchase: any = await this.heyCentricService.preBasketFind(this.companyId, this.basket, serviceItem);

      if (!purchase) {
        console.error(`No purchase found for ${this.basket} and ${serviceItem}`);
        this.showVerifyUnavailable();
        return;
      }

      // A refresh / re-visit of the return URL after the worker has finished:
      // the booking exists and was paid for — do not poll again.
      if (purchase.status === 'PAID') {
        this.alertService.clear();
        if (await this.showConfirmationForPaidPurchase(purchase)) {
          return;
        }
        this.pageTitle = this.translateService.instant('COMMON.PAYMENT_ALREADY_PAID_TITLE');
        this.ref = purchase.ref || this.basket;
        this.paymentAlreadyPaid = true;
        this.spinner.hide();
        console.warn('Purchase already paid');
        return;
      }

      if (!(purchase.data && purchase.data.client_id)) {
        console.error(`No client found for ${this.basket} and ${serviceItem}`);
        this.spinner.hide();
        this.paymentFailed = true;
        this.alertService.error(this.translateService.instant('PAYMENT.ERROR'));
        return;
      }

      await this.hydrateFromPurchase(purchase);
      purchase.data.auth_code = authCode;
      purchase.data.receipt = receiptNo;
      if (agent) {
        purchase.data.agent = true;
      }
      this.receiptId = receiptNo;
      // Do not persist RETURNED_FROM_GATEWAY here: a fast gateway callback may
      // already have advanced the record to PAID, and writing over it regresses
      // the status so the poll below never resolves. The client only polls.

      this.startPolling(serviceItem, authCode, receiptNo);
    } catch (err) {
      console.error(err);
      this.showVerifyUnavailable();
    }
  }

  // Restore the journey state the summary page persisted on the pre-basket
  // record (the redirect lost the in-memory state).
  private async hydrateFromPurchase(purchase: any): Promise<void> {
    if (purchase.data.auth_token) {
      localStorage.setItem('auth_token', purchase.data.auth_token);
    }
    const extraItems = purchase.data.extra_items;
    if (extraItems && extraItems.lookupResponses) {
      this.datastoreService.lookupResponses = extraItems.lookupResponses;
    }
    if (extraItems && extraItems.selectedAddress) {
      this.datastoreService.selectedAddress = extraItems.selectedAddress;
    }
    if (extraItems && Array.isArray(extraItems.fees)) {
      this.datastoreService.serviceFees = extraItems.fees;
    }
    if (purchase.data.journey_route) {
      this.datastoreService.journeyRoute = purchase.data.journey_route;
    }
    if (purchase.data.detail_group) {
      await this.getCompanyQuestions(purchase.data.detail_group);
    }
    this.selectedSlot = purchase.data.slot;
    this.datastoreService.extraDuration = purchase.data.extra_duration;
    await this.availabilityService.setSelectedSlot(this.selectedSlot);
  }

  private startPolling(serviceItem: string, authCode: string, receiptNo: string): void {
    this.stopPolling();
    const source = interval(2000);
    this.pollSubscription = source.subscribe(async (val) => {
      console.log(`Run ${val}`);
      if (val >= this.retries) {
        console.error(`Retry limit of ${this.retries} reached`);
        this.stopPolling();
        this.pageTitle = this.translateService.instant('COMMON.PAYMENT_STILL_CONFIRMING_TITLE');
        this.webhookResponseStatus = '';
        this.alertService.clear();
        this.maxRetriesReached = true;
        this.spinner.hide();
        return;
      }
      if (this.bookingMade) {
        return;
      }
      try {
        const found: any = await this.heyCentricService.preBasketFind(this.companyId, this.basket, serviceItem);
        console.log('found in interval', found);
        if (!found || !found.status) {
          return;
        }
        this.webhookResponseStatus = `COMMON.${found.status}`;
        if (found.data) {
          if (found.data.auth_code) {
            this.chargeId = found.data.auth_code;
          }
          if (found.data.receipt) {
            this.receipt = found.data.receipt;
          }
        }
        if (found.ref) {
          this.ref = found.ref;
        }
        if (!this.chargeId && authCode) {
          this.chargeId = authCode;
        }
        if (!this.receipt && receiptNo) {
          this.receipt = receiptNo;
        }

        if (found.status === 'PAID') {
          // Guard against a second tick arriving while the awaits below run.
          this.bookingMade = true;
          this.stopPolling();
          await this.completeBooking(found);
          return;
        }

        if (found.status.indexOf('FAILED') !== -1) {
          this.stopPolling();
          this.spinner.hide();
          this.webhookResponseStatus = '';
          this.failed = true;
          this.paymentFailed = true;
        }
      } catch (err) {
        console.error(err);
        this.alertService.clear();
        this.webhookResponseStatus = 'COMMON.PAYMENT_VERIFY_RETRYING';
      }
    });
  }

  // Load the booking the worker made for a PAID record and go to confirmation.
  private async completeBooking(found: any): Promise<void> {
    const purchase: any = await this.apiService.findPurchase(this.selectedLocation, found.data.purchase_id);
    const booking = purchase._embedded.bookings[0];
    this.datastoreService.booking = booking;
    const service = await this.apiService.getService(this.selectedLocation, booking.service_id);
    this.selectedService = service;
    this.datastoreService.selectedService = service;
    await this.servicesService.set(service);

    localStorage.setItem('booking', JSON.stringify(booking));
    localStorage.setItem('date', JSON.stringify(this.selectedSlot));
    localStorage.setItem('location', JSON.stringify(this.selectedLocation));
    localStorage.setItem('service', JSON.stringify(this.selectedService));

    this.router.navigate(['confirmation']);
  }

  // A record already PAID on arrival (a refresh after a slow verification):
  // send the customer to their confirmation instead of a dead end. Returns
  // false when the booking cannot be loaded, so the caller falls back to the
  // already-paid message.
  private async showConfirmationForPaidPurchase(purchase: any): Promise<boolean> {
    if (!purchase.data || !purchase.data.purchase_id) {
      return false;
    }
    try {
      await this.hydrateFromPurchase(purchase);
      await this.completeBooking(purchase);
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  private showVerifyUnavailable(): void {
    if (this.maxRetriesReached || this.paymentAlreadyPaid) {
      return;
    }
    this.pageTitle = this.translateService.instant('COMMON.PAYMENT_STILL_CONFIRMING_TITLE');
    this.webhookResponseStatus = '';
    this.alertService.clear();
    this.maxRetriesReached = true;
    this.spinner.hide();
  }

  // The user abandoned the HeyCentric payment (gateway Back/Cancel, or the
  // browser Back button landing here with no params). Clear the basket they
  // abandoned and restart the journey on the correct landing page.
  async restartJourneyAfterCancel() {
    try {
      await this.apiService.deleteBaskets({ id: this.companyId });
    }
    catch (err) {
      console.warn('Could not clear basket on payment cancel/back:', err);
    }
    this.basketService.currentBasket = null;
    this.restartJourney();
  }

  // Agent-aware restart target, shared by the cancel path and the failed
  // panel's Restart button.
  restartJourney() {
    const authToken: string = localStorage.getItem('auth_token');
    if (localStorage.getItem('agent') && authToken) {
      // Agent/telephony journey: return to the admin services page.
      window.location.assign(`/services?admin-only=true&auth_token=${authToken}`);
    }
    else {
      // Public journey: return to the landing page for the journey they were on.
      const journey = sessionStorage.getItem('journey');
      window.location.assign(journey ? `/start/${journey}` : `/start`);
    }
  }

  async getCompanyQuestions(detailGroup) {
    this.questionsService.getCompanyQuestions(detailGroup).subscribe((res: any) => {
      // Questions = all questions on the company
      const questions = res.questions;
      this.datastoreService.companyQuestions = questions;
    });
  }

}
